import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { contactListAPI, ContactList, Contact, uploadAPI } from '../services/api';
import Icon from '../components/Icon';
import StatTile from '../components/ui/StatTile';
import Empty from '../components/ui/Empty';
import { fmt } from '../utils/format';
import { useT } from '../i18n/LanguageContext';

/** Initials for the mini avatar from a name/email. */
function initials(name?: string | null, email?: string | null): string {
  const base = (name && name.trim()) || (email ? email.split('@')[0] : '');
  if (!base) return '?';
  const parts = base.split(/[\s._-]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return base.slice(0, 2).toUpperCase();
}

/** Deterministic token-based background for an avatar. */
const AVATAR_BGS = ['var(--brand-500)', 'var(--ch-sms)', 'var(--accent)', 'var(--brand-600)'];
function avatarBg(key?: string | null): string {
  const k = key || ''; // email may be null for phone-only contacts
  let h = 0;
  for (let i = 0; i < k.length; i++) h = (h * 31 + k.charCodeAt(i)) >>> 0;
  return AVATAR_BGS[h % AVATAR_BGS.length];
}

const ContactListDetail: React.FC = () => {
  const t = useT();
  const { id } = useParams();
  const navigate = useNavigate();

  const [list, setList] = useState<ContactList | null>(null);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    loadList();
    loadContacts();
  }, [id, page]);

  const loadList = async () => {
    try {
      const response = await contactListAPI.getOne(parseInt(id!));
      if (response.data.success) {
        setList(response.data.data.list);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const loadContacts = async () => {
    try {
      setLoading(true);
      const response = await contactListAPI.getContacts(parseInt(id!), { page, limit: 20 });

      if (response.data.success) {
        setContacts(response.data.data.contacts);
        setTotalPages(response.data.data.pages);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setUploadFile(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) {
      alert(t('Molimo izaberite Excel fajl', 'Please select an Excel file'));
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');

    try {
      const response = await contactListAPI.uploadContacts(parseInt(id!), uploadFile);
      setSuccess(t(
        `Uspešno upload-ovano ${response.data.data.totalUploaded} kontakata! (Preskočeno: ${response.data.data.skipped}, Greške: ${response.data.data.errors})`,
        `Successfully uploaded ${response.data.data.totalUploaded} contacts! (Skipped: ${response.data.data.skipped}, Errors: ${response.data.data.errors})`,
      ));
      setUploadFile(null);
      loadList();
      loadContacts();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveContact = async (contactId: number) => {
    if (!window.confirm(t('Da li ste sigurni da želite da uklonite ovaj kontakt iz liste?', 'Are you sure you want to remove this contact from the list?'))) {
      return;
    }

    try {
      await contactListAPI.removeContact(parseInt(id!), contactId);
      setSuccess(t('Kontakt uspešno uklonjen iz liste!', 'Contact removed from the list successfully!'));
      loadList();
      loadContacts();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await uploadAPI.downloadTemplate();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'contact_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err: any) {
      setError(t('Greška prilikom preuzimanja template-a', 'Error downloading the template'));
    }
  };

  const handleExport = async (format: 'xlsx' | 'csv' = 'xlsx') => {
    try {
      setError('');
      const response = await contactListAPI.exportContacts(parseInt(id!), format);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = `${list?.name.replace(/[^a-z0-9]/gi, '_')}_contacts.${format}`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setSuccess(t(
        `Lista uspešno eksportovana kao ${format.toUpperCase()}`,
        `List exported successfully as ${format.toUpperCase()}`,
      ));
    } catch (err: any) {
      setError(t('Greška prilikom eksportovanja liste', 'Error exporting the list'));
    }
  };

  if (loading && !list) {
    return (
      <div className="page page-wide">
        <div className="card card-pad muted">{t('Učitavanje…', 'Loading…')}</div>
      </div>
    );
  }

  if (!list) {
    return (
      <div className="page page-wide">
        <Empty icon="lists" title={t('Lista nije pronađena', 'List not found')} sub={t('Tražena lista kontakata ne postoji ili je obrisana.', 'The requested contact list does not exist or has been deleted.')} />
      </div>
    );
  }

  // Whole-list counts come from the API (getOne). The page-local filters are
  // only a fallback for older responses that didn't include the aggregates.
  const total = list.contactCount ?? contacts.length;
  const unsubscribed = list.unsubscribedCount ?? contacts.filter((c) => c.unsubscribed).length;
  const subscribed = list.subscribedCount ?? (list.contactCount != null ? total - unsubscribed : contacts.filter((c) => !c.unsubscribed).length);

  return (
    <div className="page page-wide">
      <button className="btn btn-ghost btn-sm" style={{ marginBottom: 16, paddingLeft: 6 }} onClick={() => navigate('/contact-lists')}>
        <Icon name="chevronLeft" size={16} /> {t('Liste kontakata', 'Contact lists')}
      </button>

      <div className="page-head">
        <div className="flex items-center gap-16">
          <span style={{ width: 52, height: 52, borderRadius: 15, background: 'var(--brand-50)', color: 'var(--brand-600)', display: 'grid', placeItems: 'center', flex: 'none' }}>
            <Icon name="lists" size={24} />
          </span>
          <div>
            <div className="page-title" style={{ fontSize: 24 }}>{list.name}</div>
            {list.description && <div className="page-sub">{list.description}</div>}
          </div>
        </div>
        <div className="flex gap-8 wrap">
          <button className="btn btn-secondary btn-sm" onClick={() => handleExport('xlsx')}>
            <Icon name="download" size={15} /> {t('Export Excel', 'Export Excel')}
          </button>
          <button className="btn btn-secondary btn-sm" onClick={() => handleExport('csv')}>
            <Icon name="download" size={15} /> {t('Export CSV', 'Export CSV')}
          </button>
        </div>
      </div>

      {error && (
        <div className="card card-pad" style={{ borderColor: 'var(--danger)', color: 'var(--danger-strong)', marginBottom: 16 }}>
          <Icon name="alert" size={16} /> {error}
        </div>
      )}
      {success && (
        <div className="card card-pad" style={{ borderColor: 'var(--ok)', color: 'var(--brand-700)', marginBottom: 16 }}>
          <Icon name="checkCircle" size={16} /> {success}
        </div>
      )}

      <div className="stat-grid cols-3" style={{ marginBottom: 18 }}>
        <StatTile icon="contacts" label={t('Članovi', 'Members')} value={fmt(total)} />
        <StatTile icon="check" channel="sms" label={t('Pretplaćeni', 'Subscribed')} value={fmt(subscribed)} />
        <StatTile icon="x" label={t('Odjavljeni', 'Unsubscribed')} value={fmt(unsubscribed)} />
      </div>

      {/* Upload / Import */}
      <div className="card card-pad" style={{ marginBottom: 18 }}>
        <div className="flex items-center justify-between wrap gap-12" style={{ marginBottom: 14 }}>
          <div className="card-title flex items-center gap-8">
            <Icon name="upload" size={18} /> {t('Upload kontakata (Excel)', 'Upload contacts (Excel)')}
          </div>
          <button className="btn btn-secondary btn-sm" onClick={handleDownloadTemplate}>
            <Icon name="download" size={15} /> {t('Preuzmi šablon', 'Download template')}
          </button>
        </div>

        <div
          className="card-hover"
          onClick={() => document.getElementById('file-input')?.click()}
          style={{ border: '2px dashed var(--border-strong)', borderRadius: 'var(--r-lg)', padding: '40px 24px', textAlign: 'center', cursor: 'pointer' }}
        >
          <input
            id="file-input"
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
          <span style={{ width: 56, height: 56, borderRadius: 16, background: 'var(--brand-50)', color: 'var(--brand-600)', display: 'grid', placeItems: 'center', margin: '0 auto 14px' }}>
            <Icon name={uploadFile ? 'checkCircle' : 'upload'} size={24} />
          </span>
          {uploadFile ? (
            <>
              <div style={{ fontWeight: 700, fontSize: 16 }}>{t('Izabran fajl:', 'Selected file:')} {uploadFile.name}</div>
              <p className="muted" style={{ fontSize: 13.5, marginTop: 6 }}>{t('Kliknite „Upload kontakata“ ispod da nastavite.', 'Click "Upload contacts" below to continue.')}</p>
            </>
          ) : (
            <>
              <div style={{ fontWeight: 700, fontSize: 16 }}>{t('Prevucite Excel fajl ili kliknite da izaberete', 'Drag an Excel file or click to select')}</div>
              <p className="muted" style={{ fontSize: 13.5, marginTop: 6 }}>{t('Podržani formati: .xlsx, .xls', 'Supported formats: .xlsx, .xls')}</p>
            </>
          )}
        </div>

        {uploadFile && (
          <div className="flex gap-8 mt-16">
            <button className="btn btn-primary" onClick={handleUpload} disabled={uploading}>
              {uploading ? t('Upload u toku…', 'Upload in progress…') : <><Icon name="upload" size={16} /> {t('Upload kontakata', 'Upload contacts')}</>}
            </button>
            <button className="btn btn-ghost" onClick={() => setUploadFile(null)}>
              {t('Otkaži', 'Cancel')}
            </button>
          </div>
        )}

        <div className="hint mt-16">
          <div className="section-title" style={{ marginBottom: 6 }}>{t('Napomena', 'Note')}</div>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            <li>{t('Excel fajl mora imati kolonu', 'The Excel file must have a')} <b>„email“</b> {t('(obavezno).', 'column (required).')}</li>
            <li>{t('Možete dodati kolonu', 'You can add a')} <b>„name“</b> {t('za ime kontakta.', 'column for the contact name.')}</li>
            <li>{t('Duplikati će biti automatski preskočeni.', 'Duplicates will be automatically skipped.')}</li>
            <li>{t('Odjavljeni kontakti neće primati email-ove.', 'Unsubscribed contacts will not receive emails.')}</li>
          </ul>
        </div>
      </div>

      {/* Members table */}
      <div className="card">
        <div className="card-pad flex items-center justify-between wrap gap-12" style={{ paddingBottom: 12 }}>
          <div className="card-title">{t('Članovi liste', 'List members')}</div>
          <span className="muted" style={{ fontSize: 13 }}>
            <b className="tnum">{fmt(contacts.length)}</b> {t('na ovoj strani', 'on this page')}
          </span>
        </div>

        {contacts.length === 0 ? (
          <Empty
            icon="contacts"
            title={t('Nema kontakata u listi', 'No contacts in the list')}
            sub={t('Upload-ujte kontakte pomoću Excel fajla da biste počeli.', 'Upload contacts using an Excel file to get started.')}
          />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th style={{ paddingLeft: 22 }}>{t('Ime', 'Name')}</th>
                <th>{t('Email', 'Email')}</th>
                <th>{t('Telefon', 'Phone')}</th>
                <th>{t('Status', 'Status')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {contacts.map((contact) => (
                <tr key={contact.id}>
                  <td style={{ paddingLeft: 22 }}>
                    <div className="flex items-center gap-12">
                      <span className="av-mini" style={{ background: avatarBg(contact.email || contact.phoneNumber || String(contact.id)) }}>
                        {initials(contact.name, contact.email)}
                      </span>
                      <span style={{ fontWeight: 600 }}>{contact.name || '—'}</span>
                    </div>
                  </td>
                  <td className="muted" style={{ fontSize: 13 }}>{contact.email || '—'}</td>
                  <td className="muted mono" style={{ fontSize: 13 }}>{contact.phoneNumber || '—'}</td>
                  <td>
                    {contact.unsubscribed ? (
                      <span className="badge badge-failed">{t('Odjavljen', 'Unsubscribed')}</span>
                    ) : (
                      <span className="badge badge-completed">{t('Pretplaćen', 'Subscribed')}</span>
                    )}
                  </td>
                  <td style={{ textAlign: 'right', paddingRight: 18 }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => handleRemoveContact(contact.id)} style={{ color: 'var(--danger)' }}>
                      <Icon name="trash" size={14} /> {t('Ukloni', 'Remove')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {totalPages > 1 && (
          <div className="card-pad flex items-center justify-between">
            <button className="btn btn-secondary btn-sm" onClick={() => setPage(page - 1)} disabled={page === 1}>
              <Icon name="chevronLeft" size={16} /> {t('Prethodna', 'Previous')}
            </button>
            <span className="muted tnum" style={{ fontSize: 13 }}>
              {t('Strana', 'Page')} {page} {t('od', 'of')} {totalPages}
            </span>
            <button className="btn btn-secondary btn-sm" onClick={() => setPage(page + 1)} disabled={page === totalPages}>
              {t('Sledeća', 'Next')} <Icon name="arrowRight" size={16} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactListDetail;
